import {
  AbsoluteCenter,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Input,
  Link,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { ExternalLinkIcon, QuestionIcon } from '@chakra-ui/icons';
import {
  createRepaymentAllocationApi,
  getRemainingInstallmentsApi,
} from 'api/dealApi';
import { getFacilityFeesApi } from 'api/facilityFeeApi';
import {
  getAccountingInvoiceApi,
  listAccountingInvoicesApi,
} from 'api/accountingInternalApi';
import { downloadInvoicePdf } from 'pages/Cockpit/AccountingInternal/calc/downloadInvoicePdf';
import {
  OBLIGATION_TYPE,
  sumObligationAmounts,
} from 'pages/Cockpit/AccountingInternal/calc/suggestObligations';
import { getEffectiveSetOffAmount } from 'pages/Cockpit/AccountingInternal/calc/setOffNotice';
import { TwDatePicker } from 'components/DatePicker';
import { Currency, currencyFormat } from 'components/Number';
import { CurrencyNumberInput } from 'components/NumberInput';
import Card from 'components/card/Card';
import SelectInput from 'components/inputs/SelectInput';
import BackLink from 'components/links/BackLink';
import { Decimal } from 'decimal.js';
import { useTwelveBankAccounts } from 'hooks/useTwelveBankAccounts';
import cloneDeep from 'lodash.clonedeep';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const Repayment = () => {
  const [loading, setLoading] = useState(true);
  const [bankAccountId, setBankAccountId] = useState('');
  const [transferDate, setTransferDate] = useState(null);
  const [repaymentAmount, setRepaymentAmount] = useState(null);
  const [setOffAmount, setSetOffAmount] = useState(0);
  const [allocations, setAllocations] = useState(null);
  const [facilityFees, setFacilityFees] = useState([]);
  // Free-text charges on the selected invoice. Shown for context only: a
  // repayment settles obligations, never charges, so they are excluded from
  // both the amount and the set-off below.
  const [invoiceCharges, setInvoiceCharges] = useState([]);
  const [selectedFeeId, setSelectedFeeId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const { bankAccountOptions, isTwelveBankAccountsLoading } =
    useTwelveBankAccounts();

  const { customerId } = useParams();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([getRemainingInstallments(), loadFacilityFees(), loadInvoices()]);
  }, []);

  const getRemainingInstallments = async () => {
    try {
      let _allocations = await getRemainingInstallmentsApi({ customerId });
      _allocations = _allocations.sort((a, b) =>
        moment(a.transferDate).diff(moment(b.transferDate))
      );
      setAllocations(initAllocations(_allocations));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to get remaining installments',
        status: 'error',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loadFacilityFees = async () => {
    try {
      const fees = await getFacilityFeesApi(customerId);
      const today = moment().format('YYYY-MM-DD');
      const unpaid = (fees || []).filter(f => f.status !== 'paid');
      const past = unpaid.filter(f => f.due_date <= today);
      const nextUpcoming = unpaid
        .filter(f => f.due_date > today)
        .sort((a, b) => a.due_date.localeCompare(b.due_date))
        .slice(0, 1);
      setFacilityFees([...past, ...nextUpcoming]);
    } catch {
      // non-critical
    }
  };

  const loadInvoices = async () => {
    try {
      const rows = await listAccountingInvoicesApi({ customerId, availableForRepayment: true });
      setInvoices(rows || []);
    } catch {
      // non-critical; the invoice picker just stays empty
    }
  };

  // Pre-fills the form from the selected invoice — installment allocations
  // and the facility fee come from the invoice's own lines, matched back
  // onto the installments/fees already loaded for this customer. Everything
  // stays editable afterward; this only sets the initial values.
  const applyInvoiceToForm = (invoice, currentAllocations) => {
    setTransferDate(invoice.dueDate || null);

    // A repayment covers the invoice's obligations only — its cohort returns
    // and facility fees. Free-text charges are settled on the invoice itself,
    // so they are left out of both the amount and the set-off here: an invoice
    // of 50k facility fee + 200k cohort + 40k lawyers fee, fully set off,
    // records a 250k repayment set off by 250k, not 290k.
    const obligationsTotal = sumObligationAmounts(invoice.lines || []);
    setRepaymentAmount(obligationsTotal || null);

    // The set-off still cannot exceed the Growth Investment requested against
    // the invoice, so cap the obligations by whatever that leaves.
    setSetOffAmount(
      invoice.setOffEnabled
        ? Math.min(obligationsTotal, getEffectiveSetOffAmount(invoice))
        : 0
    );
    setBankAccountId(invoice.receivingBankAccountId || '');

    const lines = invoice.lines || [];
    let unmatchedLines = 0;
    const invoiceInstallmentIds = new Set();

    const nextAllocations = cloneDeep(currentAllocations);
    lines
      .filter((line) => line.type === OBLIGATION_TYPE.COHORT_RETURN)
      .forEach((line) => {
        const allocation = nextAllocations.find((a) => a.id === line.installmentId);
        if (!allocation) {
          unmatchedLines += 1;
          return;
        }
        invoiceInstallmentIds.add(line.installmentId);
        allocation.amortizationId = line.amortizationId;
        allocation.amount = line.amount;
      });
    // Only installments actually on the invoice can be part of this repayment (see
    // getValidationErrors/isAllowedByInvoice below) — clear anything else so a manual
    // entry made before the invoice was selected doesn't linger as a stale, un-editable
    // amount that then fails validation with no way to fix it.
    nextAllocations.forEach((allocation) => {
      if (!invoiceInstallmentIds.has(allocation.id)) {
        allocation.amortizationId = null;
        allocation.amount = 0;
      }
    });
    setAllocations(nextAllocations);

    // A facility fee not on the invoice can't be paid through it either — this always
    // syncs (including clearing to null when the invoice has no fee line), not just
    // setting it when present, so a previously-selected fee doesn't linger.
    const feeLine = lines.find((line) => line.type === OBLIGATION_TYPE.FACILITY_FEE);
    setSelectedFeeId(feeLine ? feeLine.facilityFeeId : null);

    // Charge lines always travel with the invoice, so this syncs unconditionally
    // (including clearing to none) the same way the facility fee above does. A
    // line with no invoiceChargeId predates the charge records and can't be
    // settled, so it's left out of the total rather than silently dropped.
    setInvoiceCharges(
      lines
        .filter((line) => line.type === OBLIGATION_TYPE.OTHER_CHARGE && line.included !== false)
        .filter((line) => line.invoiceChargeId)
        .map((line) => ({
          id: line.invoiceChargeId,
          description: line.description,
          amount: Number(line.amount) || 0,
        }))
    );

    if (unmatchedLines > 0) {
      toast({
        title: 'Some invoice lines could not be pre-filled',
        description: `${unmatchedLines} installment line(s) on this invoice don't match the remaining installments — fill those in manually.`,
        status: 'warning',
      });
    }
  };

  const onSelectInvoice = async (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    if (!invoiceId) {
      setSelectedInvoice(null);
      setInvoiceCharges([]);
      return;
    }
    setLoadingInvoice(true);
    try {
      const invoice = await getAccountingInvoiceApi({ invoiceId });
      setSelectedInvoice(invoice);
      applyInvoiceToForm(invoice, allocations);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load invoice',
        status: 'error',
      });
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleViewInvoicePdf = async () => {
    if (!selectedInvoice) return;
    try {
      await downloadInvoicePdf(selectedInvoice);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to generate invoice PDF',
        status: 'error',
      });
    }
  };

  const initAllocations = (_allocations) => {
    let newAllocations = cloneDeep(_allocations);
    newAllocations.forEach((allocation) => {
      // The API returns the installment's id as `installmentId`, but every other
      // place in this component (including invoice-line matching above) reads
      // `.id` — normalize here once so the field is consistent everywhere.
      allocation.id = allocation.installmentId;
      const firstNonPaidItem = allocation.amortization?.find(
        (a) => a.actualPaidAmount === null && a.amountToClose != null
      );
      if (firstNonPaidItem) {
        allocation.amortizationId = firstNonPaidItem.id;
      }
    });
    return newAllocations;
  };

  const selectedFee = facilityFees.find(f => f.id === selectedFeeId) ?? null;

  // Once an invoice is selected, a repayment can only include installments/fee that are
  // actually on it — these drive both the disabled rows below and getValidationErrors.
  const invoiceInstallmentIds = selectedInvoice
    ? new Set(
        (selectedInvoice.lines || [])
          .filter((line) => line.type === OBLIGATION_TYPE.COHORT_RETURN)
          .map((line) => line.installmentId)
      )
    : null;
  const invoiceFeeId = selectedInvoice
    ? (selectedInvoice.lines || []).find((line) => line.type === OBLIGATION_TYPE.FACILITY_FEE)?.facilityFeeId ?? null
    : null;

  const invoiceChargesTotal = invoiceCharges.length > 0
    ? Decimal.sum(...invoiceCharges.map((charge) => charge.amount || 0))
    : new Decimal(0);

  const getSumOfAllocations = () => {
    const installmentSum = allocations.length > 0
      ? Decimal.sum(...allocations.map((a) => a.amount || 0))
      : new Decimal(0);
    const feeAmount = selectedFee ? new Decimal(selectedFee.amount || 0) : new Decimal(0);
    // Deliberately excludes invoiceChargesTotal: a repayment settles
    // obligations only, and the charge slice is settled on the invoice.
    return installmentSum.plus(feeAmount);
  };

  const hasInstallmentAllocations = () => allocations.some((a) => a.amount > 0);

  const createRepaymentAllocation = async () => {
    setLoading(true);
    try {
      // A repayment needs at least one installment allocation or a facility
      // fee — see getValidationErrors, which blocks the Apply button
      // otherwise. deal-service accepts a fee-only repayment (no
      // allocations) and marks the fee paid atomically with it (same
      // transaction, linked by repayment_id) when facilityFeeId is set.
      await createRepaymentAllocationApi({
        customerId,
        bankAccountId,
        transferDate,
        allocations,
        setOffAmount: setOffAmount || 0,
        facilityFeeId: selectedFee?.id || undefined,
        invoiceId: selectedInvoiceId || undefined,
      });

      navigate(`/operations/${customerId}/installment`);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create repayment allocation',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const getValidationErrors = () => {
    const errors = [];

    if (!bankAccountId) {
      errors.push('Select a bank account.');
    }

    if (!repaymentAmount) {
      errors.push('Enter a repayment amount.');
    }

    if (!hasInstallmentAllocations() && !selectedFee) {
      errors.push('Enter an amount for at least one installment, or select a facility fee to pay.');
    }

    if (allocations.some((a) => !a.amount && a.comment)) {
      errors.push('Remove the comment on installments with no amount, or enter an amount.');
    }

    if (allocations.filter((a) => a.amount).some((a) => !a.amortizationId)) {
      errors.push('Select an amortization period for every installment with an amount.');
    }

    if (invoiceInstallmentIds && allocations.some((a) => a.amount > 0 && !invoiceInstallmentIds.has(a.id))) {
      errors.push('This invoice does not include one or more of the selected installments — clear the invoice or their amounts.');
    }

    if (selectedInvoice && selectedFee && selectedFee.id !== invoiceFeeId) {
      errors.push('This invoice does not include the selected facility fee — clear the invoice or the fee.');
    }

    const total = getSumOfAllocations();

    if (new Decimal(setOffAmount || 0).greaterThan(total)) {
      errors.push('Set Off Amount cannot exceed the total repayment amount (installments + facility fee).');
    }

    // Compare at cent precision — amounts arriving from other services (e.g. the
    // facility fee) can carry sub-cent noise that's invisible once rounded for
    // display, and currency has no meaning below the cent anyway.
    const diff = new Decimal(repaymentAmount || 0).minus(total).abs();
    if (repaymentAmount && diff.greaterThanOrEqualTo('0.01')) {
      errors.push(
        `Repayment Amount must equal the sum of installment allocations and facility fee (off by ${currencyFormat(diff, { maximumFractionDigits: 2 })}).`
      );
    }

    return errors;
  };

  if (loading || isTwelveBankAccountsLoading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  const validationErrors = getValidationErrors();

  const generateAmortizationOptions = (amortization) => {
    if (!amortization) return [];
    return amortization.map((a) => ({
      value: a.id,
      label: (
        <Box>
          <Text fontWeight={800}>{moment(a.date).format('MM/DD/YYYY')}</Text>
          <SimpleGrid columns={2} gap={1} ml={2} mt={2} fontSize={'sm'}>
            <Text>Minimum: </Text>
            <Currency value={a.minAmount} maximumFractionDigits={2} />
            {a.revenueShareAmount && (
              <>
                <Text>Revenue: </Text>
                <Currency value={a.revenueShareAmount} maximumFractionDigits={2} />
              </>
            )}
            {a.amountToPay && (
              <>
                <Text>Expected: </Text>
                <Currency value={a.amountToPay} maximumFractionDigits={2} />
              </>
            )}
            {a.actualPaidAmount && (
              <>
                <Text>Actual: </Text>
                <Currency value={a.actualPaidAmount} maximumFractionDigits={2} />
              </>
            )}
            {a.amountToPay && a.actualPaidAmount && (
              <>
                <Text>Remaining: </Text>
                <Currency
                  value={new Decimal(a.amountToPay).minus(a.actualPaidAmount)}
                  maximumFractionDigits={2}
                />
              </>
            )}
          </SimpleGrid>
        </Box>
      ),
    }));
  };

  const generateFacilityFeeOptions = () => {
    // Once an invoice is selected, only the fee it actually lists (if any) is selectable.
    const eligibleFees = selectedInvoice
      ? facilityFees.filter((fee) => fee.id === invoiceFeeId)
      : facilityFees;
    return eligibleFees.map((fee) => ({
      value: fee.id,
      label: (
        <Box>
          <Text fontWeight={800}>{moment(fee.due_date, 'YYYY-MM-DD').format('MM/DD/YYYY')}</Text>
          <SimpleGrid columns={2} gap={1} ml={2} mt={2} fontSize={'sm'}>
            <Text>Amount: </Text>
            <Currency value={fee.amount} maximumFractionDigits={2} />
            {fee.comment && (
              <>
                <Text>Comment: </Text>
                <Text>{fee.comment}</Text>
              </>
            )}
          </SimpleGrid>
        </Box>
      ),
    }));
  };

  const generateInvoiceOptions = () =>
    invoices.map((invoice) => ({
      value: invoice.id,
      label: (
        <Box>
          <Text fontWeight={800}>{invoice.invoiceNumber}</Text>
          <SimpleGrid columns={2} gap={1} ml={2} mt={2} fontSize={'sm'}>
            <Text>Due: </Text>
            <Text>{invoice.dueDate ? moment(invoice.dueDate).format('MM/DD/YYYY') : '-'}</Text>
            <Text>Total: </Text>
            <Currency value={invoice.totalDue} maximumFractionDigits={2} />
          </SimpleGrid>
        </Box>
      ),
    }));

  const getAmountToClose = (installment) => {
    if (!installment.amortizationId) return new Decimal(0);
    return installment.amortization.find(
      (a) => a.id === installment.amortizationId
    )?.amountToClose;
  };

  return (
    <Box minW={1087} maxW={'max'} fontSize={14} mt={-10}>
      <BackLink
        text={'Back to installments table'}
        path={`/operations/${customerId}/installment`}
      />
      <Card mt={'100px'}>
        <Heading variant={'sectionHeader'} borderColor={'twelve.green.300'} mb={10}>
          Repayment
        </Heading>

        <SimpleGrid
          w={'max'}
          gridTemplateColumns={'min-content min-content min-content'}
          whiteSpace={'nowrap'}
          justifyContent={'space-between'}
          spacingY={4}
          spacingX={10}
          alignItems={'center'}
          fontSize={'sm'}
        >
          {/* The row stays put when there is nothing to pick, disabled and
              saying so. Hiding it outright left no sign the control existed —
              and an empty list is now routine, since an invoice of only
              free-text charges is settled on the invoice rather than here. */}
          <Fragment>
            <Text>Invoice</Text>
            <Flex justifyContent={'flex-end'}>
              <SelectInput
                value={selectedInvoiceId}
                options={generateInvoiceOptions()}
                isComplexOptions
                isClearable
                isDisabled={loadingInvoice || !invoices.length}
                placeholder={
                  invoices.length
                    ? 'Select invoice (optional)'
                    : 'No invoices available for repayment'
                }
                onChange={onSelectInvoice}
                w={200}
              />
            </Flex>
            {selectedInvoice ? (
              <Link onClick={handleViewInvoicePdf} color={'brand.500'} fontSize={'sm'}>
                View PDF <ExternalLinkIcon mx={'2px'} />
              </Link>
            ) : (
              <Box></Box>
            )}
          </Fragment>
          {!invoices.length && (
            <Fragment>
              <Box />
              <Text fontSize={'xs'} color={'secondaryGray.600'} whiteSpace={'normal'}>
                Paid invoices, and invoices of only free-text charges, are not
                listed — the latter are marked paid on the invoice itself.
              </Text>
              <Box />
            </Fragment>
          )}
          <Fragment>
            <Text>Bank Account</Text>
            <Flex justifyContent={'flex-end'}>
              <SelectInput
                value={bankAccountId}
                options={bankAccountOptions}
                placeholder={'Select bank account'}
                onChange={setBankAccountId}
                w={200}
              />
            </Flex>
            <Box></Box>
          </Fragment>
          <Fragment>
            <Text>Transfer Date</Text>
            <Flex justifyContent={'flex-end'}>
              <Box w={200}>
                <TwDatePicker value={transferDate} onChange={setTransferDate} />
              </Box>
            </Flex>
            <Box></Box>
          </Fragment>
          <Fragment>
            <Text>Repayment Amount</Text>
            <CurrencyNumberInput
              value={repaymentAmount}
              onChange={setRepaymentAmount}
              showPrefix={true}
              w={200}
            />
            {repaymentAmount ? (
              <Text fontWeight={800}>
                <Currency
                  value={new Decimal(repaymentAmount).minus(getSumOfAllocations())}
                  maximumFractionDigits={2}
                />
              </Text>
            ) : (
              <Box></Box>
            )}
          </Fragment>
          <Fragment>
            <Flex align={'center'} gap={1}>
              <Text>Set Off Amount</Text>
              <Tooltip
                label={'How much of the repayment amount was set off via new funding.'}
                fontSize={'xs'}
                maxW={'300px'}
              >
                <QuestionIcon boxSize={3} color={'gray.500'} cursor={'help'} />
              </Tooltip>
            </Flex>
            <CurrencyNumberInput
              value={setOffAmount}
              onChange={setSetOffAmount}
              showPrefix={true}
              w={200}
            />
            <Box></Box>
          </Fragment>
        </SimpleGrid>

        <SimpleGrid
          mt={10}
          w={'max'}
          gridTemplateColumns={'min-content min-content 280px 200px 200px min-content'}
          whiteSpace={'nowrap'}
          justifyContent={'space-between'}
          spacingY={4}
          spacingX={10}
          alignItems={'center'}
          fontSize={'sm'}
        >
          <Fragment>
            <Text mt={5}>INSTALLMENTS</Text>
            <Text mt={5}>COHORT DATES</Text>
            <Box></Box>
            <Box></Box>
            <Box></Box>
            <Text mt={5}>REMAINING</Text>
          </Fragment>
          {allocations?.map((installment, index) => {
            const isAllowedByInvoice =
              !invoiceInstallmentIds || invoiceInstallmentIds.has(installment.id);
            return (
              <Fragment key={installment.id}>
                <Text>
                  {moment(installment.transferDate).format('MMM DD, YYYY')}
                  &nbsp;
                  {`(${installment.installmentNumber}, ${currencyFormat(
                    installment.purchasePrice,
                    { maximumFractionDigits: 2 }
                  )})`}
                </Text>
                <Box>
                  {installment.cohortStartDate && installment.cohortEndDate ? (
                    <Text>
                      {`${moment(installment.cohortStartDate).format('MM/DD/YYYY')} - ${moment(installment.cohortEndDate).format('MM/DD/YYYY')}`}
                    </Text>
                  ) : 'N/A'}
                </Box>
                <SelectInput
                  placeholder={
                    isAllowedByInvoice ? 'Select amortization' : 'Not on the selected invoice'
                  }
                  isComplexOptions
                  isClearable
                  isDisabled={!isAllowedByInvoice}
                  options={generateAmortizationOptions(installment.amortization)}
                  value={allocations[index].amortizationId}
                  onChange={(value) => {
                    const _allocations = [...allocations];
                    _allocations[index].amortizationId = value;
                    setAllocations(_allocations);
                  }}
                />
                <CurrencyNumberInput
                  w={200}
                  showPrefix={true}
                  value={allocations[index].amount}
                  isDisabled={!installment.amortizationId || !isAllowedByInvoice}
                  onChange={(value) => {
                    const _allocations = [...allocations];
                    const amountToClose = getAmountToClose(installment);
                    if (amountToClose != null && value > amountToClose) {
                      value = amountToClose;
                    }
                    _allocations[index].amount = value;
                    setAllocations(_allocations);
                  }}
                />
                <Input
                  value={allocations[index].comment}
                  placeholder={'comment'}
                  onChange={(event) => {
                    const value = event.target.value;
                    const _allocations = [...allocations];
                    _allocations[index].comment = value;
                    setAllocations(_allocations);
                  }}
                />
                <Text>
                  {installment.amortizationId ? (
                    <Currency value={getAmountToClose(installment)} maximumFractionDigits={2} />
                  ) : '-'}
                </Text>
              </Fragment>
            );
          })}

          {/* Facility Fee row */}
          {facilityFees.length > 0 && (
            <Fragment>
              <Divider gridColumn={'1 / -1'} my={2} />
              <Text>FACILITY FEE</Text>
              <Box />
              <SelectInput
                placeholder={
                  selectedInvoice && !invoiceFeeId
                    ? 'Not on the selected invoice'
                    : 'Select facility fee'
                }
                isComplexOptions
                isClearable
                isDisabled={!!selectedInvoice}
                options={generateFacilityFeeOptions()}
                value={selectedFeeId}
                onChange={setSelectedFeeId}
              />
              <CurrencyNumberInput
                w={200}
                showPrefix={true}
                value={selectedFee?.amount ?? ''}
                isDisabled
              />
              <Box />
              <Text>
                {selectedFee ? (
                  <Currency value={selectedFee.amount} maximumFractionDigits={2} />
                ) : '-'}
              </Text>
            </Fragment>
          )}

          {/* Charges on the selected invoice, shown for context only. They are
              settled on the invoice, so they are excluded from the amount and
              the set-off above. */}
          {invoiceCharges.length > 0 && (
            <Fragment>
              <Divider gridColumn={'1 / -1'} my={2} />
              <Text>CHARGES (NOT IN THIS REPAYMENT)</Text>
              <Box />
              <Box />
              <Box />
              <Box />
              <Text>
                <Currency value={invoiceChargesTotal.toNumber()} maximumFractionDigits={2} />
              </Text>
              {invoiceCharges.map((charge) => (
                <Fragment key={charge.id}>
                  <Text>{charge.description}</Text>
                  <Box />
                  <Text color={'secondaryGray.600'}>Settled on the invoice</Text>
                  <CurrencyNumberInput
                    w={200}
                    showPrefix={true}
                    value={charge.amount}
                    isDisabled
                  />
                  <Box />
                  <Text>
                    <Currency value={charge.amount} maximumFractionDigits={2} />
                  </Text>
                </Fragment>
              ))}
            </Fragment>
          )}
        </SimpleGrid>

        {validationErrors.length > 0 && (
          <Box mt={10} p={4} bg={'red.50'} borderRadius={'md'} border={'1px solid'} borderColor={'red.200'}>
            {validationErrors.map((message, index) => (
              <Text key={index} color={'red.600'} fontSize={'sm'}>
                {message}
              </Text>
            ))}
          </Box>
        )}

        <Flex justify={'end'} mt={validationErrors.length > 0 ? 4 : 10}>
          <Button
            type={'submit'}
            variant={'brand'}
            w={200}
            isDisabled={validationErrors.length > 0}
            onClick={createRepaymentAllocation}
          >
            Apply
          </Button>
        </Flex>
      </Card>
    </Box>
  );
};

Repayment.propTypes = {};

export default Repayment;
